/**
 * Cache Verification Component
 *
 * Verifies that the caching system is clean, supports live rehydration/delta updates,
 * and that task counts (total and per project) are correct.
 */

import { useState, useEffect } from 'react';
import { useDatabase } from '@/context/DatabaseContext';
import type { Task } from '@/types';
import {
  getCachedTasks,
  getCacheMetadata,
  getCachedTaskCount,
  isIndexedDBAvailable,
} from '@/utils/taskCache';
import {
  getCachedSpaces,
  getCachedFolders,
  getCachedLists,
  getStructureCacheMetadata,
} from '@/utils/structureCache';
import { getRootProjectIds, getTaskCount } from '@/db/queries';
import { organizeProjectsFromDatabase } from '@/utils/organizeProjects';
import { handleError } from '@/utils/errorHandler';

interface VerificationResult {
  category: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  details?: Record<string, unknown>;
}

export function CacheVerification() {
  const { tasks } = useDatabase();
  const [results, setResults] = useState<VerificationResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [supabaseCounts, setSupabaseCounts] = useState<{
    total: number;
    bySpace: Record<string, number>;
  } | null>(null);

  const runVerification = async () => {
    setIsRunning(true);
    const verificationResults: VerificationResult[] = [];
    const EXPECTED_MIN_TASKS = 8000;
    let supabaseBySpaceCounts: Record<string, number> | null = null;

    try {
      // 1. Check IndexedDB availability
      if (!isIndexedDBAvailable()) {
        verificationResults.push({
          category: 'IndexedDB',
          status: 'fail',
          message: 'IndexedDB non disponibile nel browser',
        });
        setResults(verificationResults);
        setIsRunning(false);
        return;
      } else {
        verificationResults.push({
          category: 'IndexedDB',
          status: 'pass',
          message: 'IndexedDB disponibile',
        });
      }

      // 2. Check cache metadata
      const metadata = await getCacheMetadata();
      if (!metadata) {
        verificationResults.push({
          category: 'Cache Metadata',
          status: 'warning',
          message: 'Nessun metadata trovato nel cache (cache vuoto o non inizializzato)',
        });
      } else {
        const cacheAge = Date.now() - metadata.lastSyncTime;
        const cacheAgeMinutes = Math.floor(cacheAge / 60000);
        verificationResults.push({
          category: 'Cache Metadata',
          status: cacheAge < 10 * 60 * 1000 ? 'pass' : 'warning',
          message: `Cache sincronizzato ${cacheAgeMinutes} minuti fa`,
          details: {
            lastSyncTime: new Date(metadata.lastSyncTime).toLocaleString(),
            totalTasks: metadata.totalTasks,
            lastDeltaSyncTime: metadata.lastDeltaSyncTime
              ? new Date(metadata.lastDeltaSyncTime).toLocaleString()
              : 'Mai',
          },
        });
      }

      // 3. Check cached task count vs actual count
      const cachedTasks = await getCachedTasks();
      const cachedCount = await getCachedTaskCount();
      const actualCount = tasks.length;

      verificationResults.push({
        category: 'Task Counts',
        status: cachedCount === actualCount ? 'pass' : 'warning',
        message: `Cache: ${cachedCount}, UI: ${actualCount}`,
        details: {
          cachedCount,
          uiCount: actualCount,
          difference: Math.abs(cachedCount - actualCount),
        },
      });

      verificationResults.push({
        category: 'Expected Total Tasks',
        status: cachedCount > EXPECTED_MIN_TASKS ? 'pass' : 'fail',
        message:
          cachedCount > EXPECTED_MIN_TASKS
            ? `OK: ${cachedCount.toLocaleString()} task (attesi > ${EXPECTED_MIN_TASKS.toLocaleString()})`
            : `ERRORE: ${cachedCount.toLocaleString()} task (attesi > ${EXPECTED_MIN_TASKS.toLocaleString()})`,
        details: { cachedCount, expectedMin: EXPECTED_MIN_TASKS },
      });

      // 4. Fetch Supabase counts for comparison
      try {
        const spaceIds = await getRootProjectIds();
        const totalCount = await getTaskCount();

        const bySpace: Record<string, number> = {};
        for (const spaceId of spaceIds) {
          try {
            bySpace[spaceId] = await getTaskCount(spaceId);
          } catch (error) {
            handleError(error, { level: 'warning', message: `Errore nel conteggio dei task per lo spazio ${spaceId}` });
          }
        }

        supabaseBySpaceCounts = bySpace;
        setSupabaseCounts({ total: totalCount || 0, bySpace });

        const totalsMatch = totalCount === cachedCount && cachedCount === actualCount;

        verificationResults.push({
          category: 'Supabase Counts',
          status: totalsMatch ? 'pass' : 'warning',
          message: `Totale Supabase: ${totalCount || 0}`,
          details: {
            supabaseTotal: totalCount || 0,
            cachedTotal: cachedCount,
            uiTotal: actualCount,
            matches: totalsMatch,
          },
        });
      } catch (error) {
        verificationResults.push({
          category: 'Supabase Counts',
          status: 'fail',
          message: `Errore nel recupero dei conteggi da Supabase: ${error}`,
        });
      }

      // 5. Check structure cache
      const [cachedSpaces, cachedFolders, cachedLists, structureMetadata] = await Promise.all([
        getCachedSpaces(),
        getCachedFolders(),
        getCachedLists(),
        getStructureCacheMetadata(),
      ]);

      verificationResults.push({
        category: 'Structure Cache',
        status: 'pass',
        message: `Spazi: ${cachedSpaces.length}, Cartelle: ${cachedFolders.length}, Liste: ${cachedLists.length}`,
        details: {
          spaces: cachedSpaces.length,
          folders: cachedFolders.length,
          lists: cachedLists.length,
          lastSync: structureMetadata
            ? new Date(structureMetadata.lastSyncTime).toLocaleString()
            : 'Mai',
        },
      });

      // 6. Verify task counts per space
      const organizedSpaces = await organizeProjectsFromDatabase(cachedTasks);
      const spaceCounts: Record<string, { cached: number; organized: number; supabase?: number }> =
        {};

      for (const space of organizedSpaces) {
        const cachedSpaceTasks = cachedTasks.filter(
          (t: Task) => String(t.spaceId) === String(space.id)
        );
        spaceCounts[space.id] = {
          cached: cachedSpaceTasks.length,
          organized: space.taskCount,
          supabase: supabaseBySpaceCounts?.[space.id],
        };
      }

      const spaceCountIssues = Object.entries(spaceCounts).filter(
        ([, counts]) => counts.cached !== counts.organized
      );

      verificationResults.push({
        category: 'Space Task Counts',
        status: spaceCountIssues.length === 0 ? 'pass' : 'warning',
        message:
          spaceCountIssues.length === 0
            ? 'Tutti i conteggi per spazio sono corretti'
            : `${spaceCountIssues.length} spazi con conteggi inconsistenti`,
        details: spaceCounts,
      });

      // 7. Check for duplicate tasks in cache
      const taskIds = new Set<string>();
      const duplicates: string[] = [];
      cachedTasks.forEach((task) => {
        const taskId = task.id;
        if (!taskId) return;
        if (taskIds.has(taskId)) {
          duplicates.push(taskId);
        } else {
          taskIds.add(taskId);
        }
      });

      verificationResults.push({
        category: 'Duplicate Tasks',
        status: duplicates.length === 0 ? 'pass' : 'fail',
        message:
          duplicates.length === 0
            ? 'Nessun task duplicato trovato'
            : `${duplicates.length} task duplicati trovati`,
        details: duplicates.length > 0 ? { duplicates: duplicates.slice(0, 10) } : undefined,
      });

      // 8. Check delta update support
      const hasDeltaSync = metadata?.lastDeltaSyncTime !== undefined;
      verificationResults.push({
        category: 'Delta Updates',
        status: hasDeltaSync ? 'pass' : 'warning',
        message: hasDeltaSync
          ? 'Delta updates supportati e utilizzati'
          : 'Delta updates non ancora utilizzati (solo full sync)',
        details: {
          hasDeltaSync,
          lastDeltaSync: metadata?.lastDeltaSyncTime
            ? new Date(metadata.lastDeltaSyncTime).toLocaleString()
            : null,
        },
      });

      // 9. Verify cache consistency
      const metadataTaskCount = metadata?.totalTasks ?? 0;
      const actualCachedCount = cachedCount;
      const consistencyCheck = Math.abs(metadataTaskCount - actualCachedCount) <= 1; // Allow 1 task difference for race conditions

      verificationResults.push({
        category: 'Cache Consistency',
        status: consistencyCheck ? 'pass' : 'warning',
        message: consistencyCheck
          ? 'Cache consistente'
          : `Metadata dice ${metadataTaskCount} task, cache contiene ${actualCachedCount}`,
        details: {
          metadataTaskCount,
          actualCachedCount,
          difference: Math.abs(metadataTaskCount - actualCachedCount),
        },
      });
    } catch (error) {
      verificationResults.push({
        category: 'Error',
        status: 'fail',
        message: `Errore durante la verifica: ${error}`,
      });
    }

    setResults(verificationResults);
    setIsRunning(false);
  };

  useEffect(() => {
    // Auto-run on mount
    runVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'text-green-500';
      case 'fail':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'pass':
        return 'bg-green-500/10 border-green-500/20';
      case 'fail':
        return 'bg-red-500/10 border-red-500/20';
      case 'warning':
        return 'bg-yellow-500/10 border-yellow-500/20';
      default:
        return 'bg-gray-500/10 border-gray-500/20';
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Verifica Sistema di Caching</h1>
        <p className="text-sm text-text-tertiary">
          Verifica che il sistema di caching sia pulito, supporti reidratazione live e che i
          conteggi siano corretti
        </p>
      </div>

      <div className="mb-4">
        <button
          onClick={runVerification}
          disabled={isRunning}
          className="px-4 py-2 rounded-lg bg-accent-violet text-white text-sm font-medium hover:bg-accent-violet-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRunning ? 'Verifica in corso...' : 'Esegui Verifica'}
        </button>
      </div>

      <div className="space-y-4">
        {results.map((result, index) => (
          <div key={index} className={`p-4 rounded-lg border ${getStatusBg(result.status)}`}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-text-primary">{result.category}</h3>
              <span className={`text-sm font-medium ${getStatusColor(result.status)}`}>
                {result.status === 'pass' ? '✓' : result.status === 'fail' ? '✗' : '⚠'}
              </span>
            </div>
            <p className="text-sm text-text-secondary mb-2">{result.message}</p>
            {result.details && (
              <details className="mt-2">
                <summary className="text-xs text-text-tertiary cursor-pointer hover:text-text-secondary">
                  Dettagli
                </summary>
                <pre className="mt-2 p-2 bg-surface-secondary rounded text-xs text-text-secondary overflow-auto">
                  {JSON.stringify(result.details, null, 2)}
                </pre>
              </details>
            )}
          </div>
        ))}
      </div>

      {supabaseCounts && (
        <div className="mt-6 p-4 rounded-lg border border-border-primary bg-surface-secondary">
          <h3 className="font-semibold text-text-primary mb-2">Confronto Conteggi Supabase</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-text-secondary">Totale Supabase: </span>
              <span className="font-mono font-semibold text-text-primary">
                {supabaseCounts.total.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Totale Cache: </span>
              <span className="font-mono font-semibold text-text-primary">
                {tasks.length.toLocaleString()}
              </span>
            </div>
            <div className="mt-4">
              <span className="text-text-secondary">Per Spazio:</span>
              <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                {Object.entries(supabaseCounts.bySpace).map(([spaceId, count]) => {
                  const cachedCount = tasks.filter(
                    (t) => String(t.spaceId) === String(spaceId)
                  ).length;
                  const match = count === cachedCount;
                  return (
                    <div
                      key={spaceId}
                      className={`text-xs p-2 rounded ${match ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}
                    >
                      <span className="font-mono">{spaceId.slice(0, 8)}...</span>{' '}
                      <span className={match ? 'text-green-500' : 'text-yellow-500'}>
                        Supabase: {count} | Cache: {cachedCount}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
